import { useImperativeHandle, useLayoutEffect, useRef, type CSSProperties, type Ref, type TextareaHTMLAttributes } from "react";
import { C } from "../tokens";

/** Shared wrapping, sizing and mention paint for every Ask entry point. */
export function AskTextInput({ ref, parts, style, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  ref?: Ref<HTMLTextAreaElement>;
  parts: { text: string; selected: boolean }[];
}) {
  const input = useRef<HTMLTextAreaElement>(null);
  const display = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => input.current!, []);

  function syncDisplay() {
    if (!input.current || !display.current) return;
    // clientWidth excludes the scrollbar, so both layers wrap at the same
    // character even on platforms with non-overlay scrollbars.
    display.current.style.width = `${input.current.clientWidth}px`;
    display.current.scrollTop = input.current.scrollTop;
  }

  useLayoutEffect(() => {
    const element = input.current!;
    function resize() {
      const css = getComputedStyle(element);
      const padding = parseFloat(css.paddingTop) + parseFloat(css.paddingBottom);
      const border = parseFloat(css.borderTopWidth) + parseFloat(css.borderBottomWidth);
      const lineHeight = parseFloat(css.lineHeight);
      const maxHeight = lineHeight * 7 + padding + border;
      const scrollTop = element.scrollTop;
      element.style.height = "auto";
      element.style.maxHeight = `${maxHeight}px`;
      const contentHeight = element.value ? element.scrollHeight : lineHeight + padding;
      element.style.height = `${Math.min(contentHeight + border, maxHeight)}px`;
      element.scrollTop = scrollTop;
      syncDisplay();
    }
    resize();
    // Reflow after a sidebar/viewport resize or after the webfont loads too.
    let width = element.getBoundingClientRect().width;
    let frame = 0;
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => {
      const nextWidth = element.getBoundingClientRect().width;
      if (width !== nextWidth) {
        width = nextWidth;
        cancelAnimationFrame(frame);
        // Resizing in the observer delivery can invalidate an ancestor's
        // observer (the chat's composer clearance), producing a resize loop.
        frame = requestAnimationFrame(resize);
      }
    });
    observer?.observe(element);
    document.fonts?.addEventListener("loadingdone", resize);
    return () => {
      observer?.disconnect();
      cancelAnimationFrame(frame);
      document.fonts?.removeEventListener("loadingdone", resize);
    };
  }, [props.value]);

  // A font shorthand must precede the longhands: React preserves key order,
  // and a trailing `font: inherit` would silently reset lineHeight to normal.
  const { font, ...inputStyle } = style ?? {};
  const textStyle: CSSProperties = {
    ...(font ? { font } : {}),
    fontFamily: "inherit", fontSize: "inherit", lineHeight: 1.5,
    margin: 0, padding: 0, border: 0, ...inputStyle,
    boxSizing: "border-box", width: "100%", minWidth: 0,
    whiteSpace: "pre-wrap", overflowWrap: "anywhere", textOverflow: "clip", textAlign: "left",
  };
  return <div style={{ position: "relative", display: "flex", minWidth: 0 }}>
    <div ref={display} aria-hidden="true" data-testid="ask-mention-display" style={{
      ...textStyle, position: "absolute", inset: 0, pointerEvents: "none",
      overflow: "hidden", color: style?.color ?? C.ink,
    }}>
      {parts.map((part, i) => <span key={i} style={part.selected ? { color: C.goldInk } : undefined}>{part.text}</span>)}
      {/* Preserve the final empty line so scroll positions match the textarea. */}
      <span>{"\u200b"}</span>
    </div>
    <textarea {...props} ref={input} rows={1} wrap="soft"
      style={{ ...textStyle, position: "relative", resize: "none", overflowX: "hidden", overflowY: "auto",
        background: "transparent", color: props.value ? "transparent" : textStyle.color,
        caretColor: style?.color ?? C.ink }}
      onScroll={event => { syncDisplay(); props.onScroll?.(event); }}
      onKeyDown={event => {
        // Let the browser insert a newline, and never submit an IME candidate.
        if (event.nativeEvent.isComposing || (event.key === "Enter" && event.shiftKey)) return;
        props.onKeyDown?.(event);
      }} />
  </div>;
}
